import { Phone, SearchIcon, UserIcon, XIcon } from "@/components/icons";
import { Button, Input, Skeleton, useDebounceCallback } from "@/components/kit";
import { cn } from "@/lib/utils";
import type { CustomerDTO } from "@/modules/customers/domain/types";
import { useCustomerSearch } from "@/modules/customers/presentation/api/queries";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

type CustomerSelectPanelProps = {
  label: string;
  description: string;
  selected: CustomerDTO | null;
  onSelect: (customer: CustomerDTO) => void;
  onClear: () => void;
  excludeId?: string;
  tone?: "source" | "target";
};

export function CustomerSelectPanel({
  label,
  description,
  selected,
  onSelect,
  onClear,
  excludeId,
  tone = "source",
}: CustomerSelectPanelProps) {
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const debouncedSearch = useDebounceCallback((value: string) => setQuery(value), 350);
  const results = useCustomerSearch(query);

  const suggestions = (results.data?.data ?? []).filter(
    (customer) => customer.id !== excludeId,
  );

  return (
    <section
      className={cn(
        "brand-card flex flex-col gap-4 p-4 sm:p-5",
        tone === "source" && "border-destructive/20",
        tone === "target" && "border-leaf/25",
      )}
    >
      <div>
        <p className="font-medium">{label}</p>
        <p className="mt-1 text-muted-foreground text-sm">{description}</p>
      </div>

      {selected ? (
        <div
          className={cn(
            "flex items-start justify-between gap-3 rounded-2xl px-4 py-3",
            tone === "source" ? "bg-destructive/5" : "bg-leaf/5",
          )}
        >
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-2xl bg-background/80 text-secondary shadow-sm">
              <UserIcon className="size-4" />
            </div>
            <div className="min-w-0">
              <p className="truncate font-medium">{selected.name}</p>
              {selected.phone ? (
                <p className="mt-0.5 flex items-center gap-1.5 text-muted-foreground text-sm">
                  <Phone className="size-3.5 shrink-0" />
                  {selected.phone}
                </p>
              ) : null}
              <Link
                to="/app/customers/$id"
                params={{ id: selected.id }}
                className="mt-1 inline-flex text-secondary text-xs hover:underline"
              >
                ເບິ່ງໂປຣໄຟລ໌
              </Link>
            </div>
          </div>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            className="rounded-full"
            onClick={onClear}
          >
            <XIcon className="size-4" />
          </Button>
        </div>
      ) : (
        <>
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="rounded-full pl-9"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                debouncedSearch(e.target.value);
              }}
              placeholder="ຄົ້ນຫາຊື່ ຫຼື ເບີໂທ..."
              autoComplete="off"
            />
          </div>

          {results.isLoading && query.trim().length >= 2 ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 rounded-xl" />
              <Skeleton className="h-12 rounded-xl" />
            </div>
          ) : null}

          {query.trim().length >= 2 && !results.isLoading ? (
            suggestions.length > 0 ? (
              <div className="flex flex-col gap-1 rounded-xl border border-border/60 bg-card p-1">
                {suggestions.map((customer) => (
                  <button
                    key={customer.id}
                    type="button"
                    className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-secondary/10"
                    onClick={() => {
                      onSelect(customer);
                      setSearchInput("");
                      setQuery("");
                    }}
                  >
                    <span className="font-medium">{customer.name}</span>
                    <span className="text-muted-foreground text-xs">
                      {customer.phone ?? "—"}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="rounded-xl bg-muted/30 px-3 py-2 text-muted-foreground text-sm">
                ບໍ່ພົບລູກຄ້າ — ລອງຄົ້ນຫາອີກຄັ້ງ
              </p>
            )
          ) : (
            <p className="text-muted-foreground text-sm">
              ພິມຢ່າງໜ້ອຍ 2 ຕົວອັກສອນເພື່ອຄົ້ນຫາ
            </p>
          )}
        </>
      )}
    </section>
  );
}
